import { Button, Modal, Spinner, TextArea, Typography, Icon, TextField } from '@scale/scaleui';
import { useState } from 'react';
import { useRemoveUsersFromFlow } from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';

interface RemoveWorkerModalProps {
  isOpen: boolean;
  onClose: () => void;
  flowName: string;
  flowId: string;
}

export const RemoveWorkerModal: React.FC<RemoveWorkerModalProps> = ({
  isOpen,
  onClose,
  flowName,
  flowId,
}) => {
  const [contributors, setContributors] = useState<string>('');
  const [reason, setReason] = useState<string>('');

  const { mutate, data, error, isLoading, isError } = useRemoveUsersFromFlow();

  const handleSubmit = () => {
    const ids = contributors.split(',').map(id => id.trim());
    mutate({
      userIds: ids,
      flowId,
      reason: reason || 'Removed via OCP UI',
      batchSize: 100,
    });
  };

  const handleClose = () => {
    setContributors('');
    setReason('');
    onClose();
  };

  return (
    <Modal open={isOpen} onClose={handleClose} title={`Remove Contributor(s) from ${flowName}`}>
      <Typography.Paragraph3>
        Enter comma-separated Contributor IDs to remove from this flow. This will clear their
        onboarding state and remove them from the flow allowing them to bypass OCP.
      </Typography.Paragraph3>
      <TextArea
        className="my-4"
        id="contributors-text-area"
        title="Contributors to Remove"
        value={contributors}
        onChange={e => setContributors(e.target.value)}
        rows={4}
      />

      <div className="mb-4">
        <Typography.Paragraph3 className="font-semibold mb-2">
          Removal Reason (Optional)
        </Typography.Paragraph3>
        <TextField
          id="reason-text-field"
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g., User requested removal, Quality concerns, etc."
        />
      </div>

      {isError && error && (
        <div className="flex items-center gap-x-2 my-2">
          <Icon icon="exclamation-circle" variant="danger" />
          <Typography.Paragraph3 className="text-red-500 m-0">
            {error.message || 'Failed to remove users from flow'}
          </Typography.Paragraph3>
        </div>
      )}

      <div className="flex justify-end gap-2">
        {isLoading && <Spinner className="mr-2" size={'sm'} />}
        <Button variant="neutral" onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          disabled={isLoading || !contributors.trim()}
          onClick={handleSubmit}
          variant="danger"
        >
          Remove
        </Button>
      </div>

      {data && (
        <div className="flex flex-col gap-y-2 my-2 p-3 bg-gray-50 rounded">
          {data.details.removed.length > 0 && (
            <Typography.Paragraph3 className="text-green-600 m-0">
              ✓ Successfully removed {data.details.removed.length} contributor(s)
            </Typography.Paragraph3>
          )}
          {data.details.notFound.length > 0 && (
            <Typography.Paragraph3 className="text-yellow-600 m-0">
              ⚠ {data.details.notFound.length} contributor(s) not found in OCP
            </Typography.Paragraph3>
          )}
          {data.details.flowMismatch.length > 0 && (
            <Typography.Paragraph3 className="text-orange-600 m-0">
              ⚠ {data.details.flowMismatch.length} contributor(s) skipped - they are in a different
              flow
            </Typography.Paragraph3>
          )}
          {data.details.failed.length > 0 && (
            <Typography.Paragraph3 className="text-red-600 m-0">
              ✗ {data.details.failed.length} contributor(s) failed to remove
            </Typography.Paragraph3>
          )}
        </div>
      )}
    </Modal>
  );
};
